import React, { useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  Icon,
  KeyValue,
  Link,
  Pill,
  SectionHeader,
  Typography,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';


import { makeStyles } from 'tss-react/mui';
import { useHistory, useParams } from 'react-router-dom';
import AmendedSubjectExamFees from './AmendedSubjectExamFees';
import {
  useGetExamAmendmentDownloadSummary,
  useGetExamAmendmentSummary,
  usePostCreateExamAmendment,
} from '../../../api/candidate/exam/@candidate/queries-services/queries';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import PersonalDetails from '../../examRegistration/components/particulars/PersonalDetails';
import ContactDetails from '../../examRegistration/components/particulars/ContactDetails';
import RouteKey from '../../../routes/key';
import NextOfKinContactDetails from '../../examRegistration/components/particulars/NextOfKinContactDetails';
import { SubjectAmendmentSummaryResponse } from '../../../api/candidate/exam/model';
import EntryProofDownload from './EntryProofDownload';
import AmendmentTransaction from './AmendmentTransaction';
import { FileUploadState } from '../../../models/File';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';
import {typeTheme} from "web-library/theme/helper/tssHelper"

const useStyles = makeStyles()((theme) =>
({
  downloadPdf: {
    display: 'flex',
    paddingLeft: theme.spacing(3),
  },
  downloadPdfText: {
    fontWeight: 800,
    paddingLeft: theme.spacing(1),
  },
  paymentContainer: {
    marginBottom: theme.spacing(2),
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  backButton: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
      height: 'fit-content',
    },
  },
  tableCaption: {
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(24),
    display: 'inline-flex',
    '> *':{
      display: 'flex'
    }
  },
  sendEmailButton: {
    color: theme.color.link.linkDefault01,
    padding: 0,
    minWidth: 0,
    height: 0,
    '& p': {
       ...typeTheme(theme, 'buttonMedium'),
    },
  },
  divider: {
    paddingTop: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(-2),
    },
  },
  amendmentError: {
    marginBottom: theme.spacing(3),
  },
}));

const { amendment, exam, withdrawal, error } = RouteKey.child;
const { registration, examRegistrations, coordinatorExamRegistrations } = RouteKey.amendment.child;
const { examRegistrations: withdrawalRoute, instruction } = RouteKey.withdrawal.child;

const getBackToButtonLabel = (isGroup: boolean, t: any): string =>
  isGroup
    ? t('amendment->>GroupAmendmentReceipt.ViewSubjectAmendmentApplications')
    : t('exam->>RegistrationSummary.BackToRegisteredExams');
const getBackToListingUrl = (isGroup: boolean): string =>
  isGroup
    ? `/${amendment}/${coordinatorExamRegistrations.route}`
    : `/${exam}/${examRegistrations.route}`;

const additionalComponentRenderer = (
  theme: AugmentedTheme,
  classes: Record<string,string>,
  t: any,
  refetch: () => void,
  isDownloading: boolean = false
) => {
  if (isDownloading) {
    return <Icon name={IconName.loadingIndicator} size="L" color={theme.color.link.linkActive02} />;
  }
  return (
    <Link className={classes.downloadPdf} id="link-download" onClick={() => refetch()}>
      <Icon name={IconName.download} size="M" color={theme.color.link.linkDefault01} />
      <span className={classes.downloadPdfText}>
        {t('exam->>RegistrationSummary.DownloadSummary')}
      </span>
    </Link>
  );
};

const DisplayApplicationStatus = ({ applicationStatus }: { applicationStatus: string }) => (
  <Grid>
    {applicationStatus === 'Registered' && <Pill label={applicationStatus} variant="positive" />}
    {applicationStatus === 'Pending payment' && <Pill label={applicationStatus} variant="info" />}
  </Grid>
);

const ViewDetails = ({
  isGroup = false,
  isPaymentLink = false,
}: {
  isGroup?: boolean;
  isPaymentLink?: boolean;
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const { uuid: applicationUUID } = useParams<any>();
  const history = useHistory();
  const [amendmentError, setAmendmentError] = useState<string>();
  const [summary, setSummary] = useState<SubjectAmendmentSummaryResponse>();
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);

  const { mutate, status } = usePostCreateExamAmendment(
    () => {
      history.push(`/${amendment}/${examRegistrations.route}/${registration.route}`);
    },
    ({ response }) => {
      if (response.data?.code === '1604') {
        setAmendmentError(t('exam->>RegistrationSummary.RegistrationIsProcessing'));
      } else {
        setAmendmentError(response?.data.message);
      }
    }
  );
  useGetExamAmendmentSummary(
    (res) => setSummary(res),
    (res) => history.push(`/${error}/${res.response?.status}`),
    applicationUUID
  );
  const { refetch, isFetching } = useGetExamAmendmentDownloadSummary(
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    },
    applicationUUID
  );

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  return (
    <>
      {summary && (
        <ContentTemplate type="full">
          <SectionHeader
            title={t('amendment->>ViewDetails.Registration Details')}
          >{additionalComponentRenderer(
              theme,
              classes,
              t,
              refetch,
              isFetching
          )}</SectionHeader>
          <Grid container direction="row">
            <Grid md={5} sm={12} xs={12}>
              <KeyValue
                id="applicationid"
                keyText={t('amendment->>ViewDetails.Application ID')}
                valueText={summary.applicationStatus.applicationID}
              />
            </Grid>
            <Grid md={5} sm={12} xs={12}>
              <KeyValue
                id="nationality"
                keyText={t('amendment->>ViewDetails.Status')}
                valueText={
                  <DisplayApplicationStatus
                    applicationStatus={summary.applicationStatus.applicationStatus}
                  />
                }
              />
            </Grid>
          </Grid>
          <KeyValue
            id="applicationDateTime"
            keyText={t('amendment->>ViewDetails.Application Date/Time')}
            valueText={stringToLongDateAndShortTime(summary.applicationStatus.applicationDateTime)}
          />
          {!isGroup && <EntryProofDownload classes={classes} summary={summary} />}
          <PersonalDetails examCandidateDetail={summary.candidateDetails} />
          <ContactDetails contactDetails={summary.candidateDetails} />
          <NextOfKinContactDetails {...summary.candidateDetails} />
          <AmendedSubjectExamFees {...summary} />
          <Divider />
          <AmendmentTransaction
            classes={classes}
            applicationUUID={applicationUUID}
            isGroup={isGroup}
            summary={summary}
          />
          {!isPaymentLink && (
            <Box>
              {amendmentError && (
                <Typography variant="body2" color="error" className={classes.amendmentError}>
                  {amendmentError}
                </Typography>
              )}
              <Grid container direction="row" alignItems="flex-start" spacing={3}>
                <Grid xs={12} md={5}>
                  <Button
                    id="button-selectSubject-back"
                    fullWidth
                    // className={classes.button}
                    variant="outlined"
                    onClick={() => history.push(getBackToListingUrl(isGroup))}
                  >
                    {getBackToButtonLabel(isGroup, t)}
                  </Button>
                </Grid>
                {!isGroup && (
                  <Grid xs={12} md={4}>
                    {applicationUUID && summary.amendmentWindowOpened && (
                      <ApiButton
                        id="button-amend-subject"
                        fullWidth
                        // className={classes.button}
                        type="button"
                        status={status}
                        onClick={() => mutate({ uuid: applicationUUID })}
                      >
                        {t('exam->>RegistrationSummary.AmendSubjects')}
                      </ApiButton>
                    )}
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          {!isPaymentLink && !isGroup && summary.withdrawalWindowOpened && (
            <Box className={classes.tableCaption}>
              <Typography variant="caption"
                          // className={classes.dialogBold}
              >
                {t('exam->>RegistrationSummary.WithdrawInstruction')}&nbsp;
              </Typography>
              <Link
                id="button-withdraw-examination"
                onClick={() => {
                  history.push(`/${withdrawal}/${withdrawalRoute.route}/${instruction.route}`);
                }}
              >
                <Typography variant="caption"
                            // className={classes.dialogBold}
                >
                  {t('exam->>ResultDetail.click here.')}
                </Typography>
              </Link>
            </Box>
          )}
          {fileStatus === FileUploadState.FAILED && (
            <DownloadProgressModal
              modalState
              fileDownloadStatus={FileUploadState.FAILED}
              onDialogClose={handleCloseDownloadErrorPrompt}
            />
          )}
        </ContentTemplate>
      )}
    </>
  );
};

export default ViewDetails;
